/**
 * One interaction item on a participant's phone.
 *
 * Four types, one component, because they differ only in how an answer is
 * collected. Sequencing is the interesting one: drag-and-drop is unusable on a
 * phone with gloves on, so it uses up/down buttons instead. Slower to operate
 * and far more likely to actually work in the room.
 */
import { useEffect, useState } from 'react';

export function QuestionCard({ question, disabled, onSubmit }) {
  const [choice, setChoice] = useState('');
  const [text, setText] = useState('');
  const [order, setOrder] = useState([]);
  const [touched, setTouched] = useState(false);

  // A new item wipes the previous answer. Without this, moving from question 3
  // to question 4 would leave the last selection sitting there looking chosen.
  useEffect(() => {
    setChoice('');
    setText('');
    setOrder(question?.options?.map((o) => o.value) ?? []);
    setTouched(false);
  }, [question?.id]);

  if (!question) return null;

  function move(index, delta) {
    const next = [...order];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setOrder(next);
    setTouched(true);
  }

  function submit(event) {
    event.preventDefault();
    if (question.type === 'free_text') return onSubmit({ text });
    if (question.type === 'sequencing') return onSubmit({ order });
    return onSubmit({ choice });
  }

  const labelFor = (value) => question.options.find((o) => o.value === value)?.label ?? value;
  const ready =
    question.type === 'free_text' ? text.trim().length > 0
      : question.type === 'sequencing' ? touched
      : choice !== '';

  return (
    <form className="qcard" onSubmit={submit}>
      <h2 className="qcard__stem">{question.stem}</h2>

      {(question.type === 'multiple_choice' || question.type === 'true_false') && (
        <fieldset className="qcard__options">
          <legend className="visually-hidden">Choose one</legend>
          {question.options.map((o) => (
            <label key={o.value} className={choice === o.value ? 'qopt qopt--on' : 'qopt'}>
              <input
                type="radio"
                name="choice"
                value={o.value}
                checked={choice === o.value}
                onChange={() => setChoice(o.value)}
                disabled={disabled}
              />
              <span>{o.label}</span>
            </label>
          ))}
        </fieldset>
      )}

      {question.type === 'sequencing' && (
        <ol className="qrank">
          {order.map((value, index) => (
            <li key={value} className="qrank__item">
              <span className="qrank__pos">{index + 1}</span>
              <span className="qrank__label">{labelFor(value)}</span>
              <span className="qrank__moves">
                <button type="button" className="btn btn--quiet" disabled={disabled || index === 0}
                        onClick={() => move(index, -1)}>
                  ↑<span className="visually-hidden"> move {labelFor(value)} up</span>
                </button>
                <button type="button" className="btn btn--quiet" disabled={disabled || index === order.length - 1}
                        onClick={() => move(index, 1)}>
                  ↓<span className="visually-hidden"> move {labelFor(value)} down</span>
                </button>
              </span>
            </li>
          ))}
        </ol>
      )}

      {question.type === 'free_text' && (
        <textarea
          className="qcard__text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={4}
          maxLength={2000}
          disabled={disabled}
          placeholder="Type your answer…"
          aria-label={question.stem}
        />
      )}

      <button type="submit" className="btn btn--primary" disabled={disabled || !ready}>
        {disabled ? 'Sending…' : 'Send answer'}
      </button>
    </form>
  );
}

export default QuestionCard;
